import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Dumbbell, ChevronDown } from 'lucide-react';
import { useResults } from '../lib/useResults';
import { SaveResultButton } from '../components/tools/SaveResultButton';
import { CalculatorJsonLd } from '../components/seo/SchemaMarkup';
import { ToolSEOWrapper } from '../components/tools/ToolSEOWrapper';
import { EmailResultsGate } from '../components/tools/EmailResultsGate';

const ORM_SEO = {
  name: "One Rep Max (1RM) Calculator",
  description: "Calculate your estimated one-rep max for any lift using the Epley, Brzycki, and Lombardi formulas. Get a full percentage-based training chart.",
  url: "https://ninjathlete.com/tools/one-rep-max",
  steps: [
    { name: "Select your exercise", text: "Choose from Bench Press, Back Squat, Deadlift, Overhead Press, Barbell Row, Power Clean, or Other." },
    { name: "Enter the weight lifted", text: "Input the weight you lifted in lbs or kg." },
    { name: "Enter reps completed", text: "Input the number of reps you completed (2-12 recommended for accuracy)." },
    { name: "Calculate 1RM", text: "Click Calculate to see your estimated one-rep max using three scientific formulas and a full percentage training chart." },
  ],
  faqs: [
    { question: "What is a one-rep max (1RM)?", answer: "A one-rep max is the maximum amount of weight you can lift for a single repetition with proper form. It's used to program training loads and track strength progress." },
    { question: "How accurate are 1RM calculators?", answer: "1RM calculators are most accurate with 2-6 rep sets. Accuracy decreases with higher rep counts. We use three formulas (Epley, Brzycki, Lombardi) and average them for best results." },
    { question: "How often should I test my 1RM?", answer: "Test your true 1RM every 8-12 weeks. Use calculator estimates for daily programming. Always warm up thoroughly and have a spotter for max attempts." },
  ],
};

const EXERCISES = [
  { value: 'bench', label: 'Bench Press' },
  { value: 'squat', label: 'Back Squat' },
  { value: 'deadlift', label: 'Deadlift' },
  { value: 'ohp', label: 'Overhead Press' },
  { value: 'row', label: 'Barbell Row' },
  { value: 'clean', label: 'Power Clean' },
  { value: 'other', label: 'Other Exercise' },
];

// Epley, Brzycki, Lombardi formulas
const formulas = {
  epley: (w, r) => w * (1 + r / 30),
  brzycki: (w, r) => w * (36 / (37 - r)),
  lombardi: (w, r) => w * Math.pow(r, 0.10),
};

const percentages = [100, 95, 90, 85, 80, 75, 70, 65, 60];
const repRanges = {
  100: '1', 95: '2', 90: '3-4', 85: '5-6', 80: '7-8', 75: '9-10', 70: '11-12', 65: '13-15', 60: '16-20'
};

export default function OneRepMaxPage() {
  const [exercise, setExercise] = useState('bench');
  const [weight, setWeight] = useState('');
  const [reps, setReps] = useState('');
  const [unit, setUnit] = useState('lbs');
  const [result, setResult] = useState(null);
  const [exOpen, setExOpen] = useState(false);
  const [saved, setSaved] = useState(false);
  const { saveResult } = useResults();

  const calculate = () => {
    if (!weight || !reps || parseInt(reps) < 1) return;
    const w = parseFloat(weight);
    const r = parseInt(reps);
    if (r === 1) {
      setResult({ oneRM: Math.round(w), formula: 'Direct', table: percentages.map(p => ({ pct: p, weight: Math.round(w * p / 100), reps: repRanges[p] })) });
      return;
    }
    const epley = Math.round(formulas.epley(w, r));
    const brzycki = Math.round(formulas.brzycki(w, r));
    const lombardi = Math.round(formulas.lombardi(w, r));
    const avg = Math.round((epley + brzycki + lombardi) / 3);
    const table = percentages.map(p => ({ pct: p, weight: Math.round(avg * p / 100), reps: repRanges[p] }));
    setResult({ oneRM: avg, epley, brzycki, lombardi, formula: 'Average', table });
    setSaved(false);
  };

  const selectedExercise = EXERCISES.find(e => e.value === exercise);

  return (
    <ToolSEOWrapper toolKey="one-rep-max">
    <main className="pt-20 min-h-screen" data-testid="one-rep-max-page">
      <CalculatorJsonLd {...ORM_SEO} calculatorName={ORM_SEO.name} />
      <section className="container-ninja section-spacing">
        <Link to="/tools" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition-colors mb-8">
          <ArrowLeft className="w-4 h-4" /><span className="text-sm uppercase tracking-wider font-heading">Back to Tools</span>
        </Link>
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-block px-3 py-1 text-xs uppercase tracking-wider bg-[#E63946] text-white mb-4">Calculator</span>
            <h1 className="text-4xl md:text-5xl text-white font-heading mb-3" data-testid="orm-calc-title">One Rep Max Calculator</h1>
            <p className="text-zinc-400">Estimate your 1RM and get a full percentage-based training chart</p>
          </div>

          <div className="bg-zinc-900/80 border border-zinc-800 p-6 md:p-8">
            {/* Exercise Select */}
            <div className="relative mb-4" data-testid="exercise-select">
              <label className="block text-zinc-400 text-xs uppercase tracking-wider mb-2 font-heading">Exercise</label>
              <button type="button" onClick={() => setExOpen(!exOpen)} className="w-full flex items-center justify-between px-4 py-3 bg-zinc-800 border border-zinc-700 text-white text-sm hover:border-[#E63946]/50 transition-colors">
                <span className="flex items-center gap-2"><Dumbbell className="w-4 h-4 text-[#E63946]" />{selectedExercise?.label}</span>
                <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${exOpen ? 'rotate-180' : ''}`} />
              </button>
              {exOpen && (
                <div className="absolute z-20 w-full mt-1 bg-zinc-800 border border-zinc-700 shadow-xl">
                  {EXERCISES.map(e => (
                    <button key={e.value} type="button" onClick={() => { setExercise(e.value); setExOpen(false); }}
                      className={`w-full text-left px-4 py-3 text-sm ${e.value === exercise ? 'bg-[#E63946]/20 text-white' : 'text-zinc-300 hover:bg-zinc-700'}`}>
                      {e.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div>
                <label className="block text-zinc-400 text-xs uppercase tracking-wider mb-2 font-heading">Weight Lifted</label>
                <input type="number" value={weight} onChange={e => setWeight(e.target.value)} placeholder="Enter weight"
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 text-white text-sm placeholder-zinc-500 focus:border-[#E63946] focus:outline-none"
                  data-testid="orm-weight-input" />
              </div>
              <div>
                <label className="block text-zinc-400 text-xs uppercase tracking-wider mb-2 font-heading">Reps Completed</label>
                <input type="number" value={reps} onChange={e => setReps(e.target.value)} placeholder="1-15"
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 text-white text-sm placeholder-zinc-500 focus:border-[#E63946] focus:outline-none"
                  data-testid="orm-reps-input" />
              </div>
              <div>
                <label className="block text-zinc-400 text-xs uppercase tracking-wider mb-2 font-heading">Unit</label>
                <div className="flex gap-2">
                  {['lbs', 'kg'].map(u => (
                    <button key={u} onClick={() => setUnit(u)} className={`flex-1 py-3 text-sm font-heading uppercase transition-colors ${unit === u ? 'bg-[#E63946] text-white' : 'bg-zinc-800 text-zinc-400 border border-zinc-700'}`}>{u}</button>
                  ))}
                </div>
              </div>
            </div>
            <button onClick={calculate} disabled={!weight || !reps}
              className="w-full py-4 bg-gradient-to-r from-[#E63946] to-[#ff6b35] text-white font-heading text-lg uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              data-testid="calculate-orm-btn">
              Calculate 1RM
            </button>
          </div>

          {result && (
            <div className="mt-8" data-testid="orm-results">
              <EmailResultsGate
                toolId="one-rep-max"
                toolName="One Rep Max"
                resultPayload={{
                  exercise: selectedExercise?.label,
                  oneRM: result.oneRM,
                  unit,
                  weight: parseFloat(weight),
                  reps: parseInt(reps),
                  epley: result.epley,
                  brzycki: result.brzycki,
                  lombardi: result.lombardi,
                }}
              >
                <div className="space-y-6 animate-entrance">
                  <div className="bg-zinc-900/80 border border-[#E63946]/30 p-6 text-center">
                    <p className="text-zinc-500 text-xs uppercase tracking-wider mb-1">Estimated One Rep Max ({selectedExercise?.label})</p>
                    <p className="text-6xl font-heading text-white" data-testid="orm-value">{result.oneRM} <span className="text-2xl text-[#E63946]">{unit}</span></p>
                    {result.epley && (
                      <div className="flex justify-center gap-6 mt-4 text-xs text-zinc-500">
                        <span>Epley: {result.epley}</span>
                        <span>Brzycki: {result.brzycki}</span>
                        <span>Lombardi: {result.lombardi}</span>
                      </div>
                    )}
                  </div>
                  {/* Percentage Table */}
                  <div className="bg-zinc-900/80 border border-zinc-800 p-6">
                    <h3 className="text-white text-lg font-heading mb-4">Training Percentage Chart</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead><tr className="border-b border-zinc-700">
                          <th className="py-2 text-left text-zinc-500 font-heading">%1RM</th>
                          <th className="py-2 text-left text-zinc-500 font-heading">Weight ({unit})</th>
                          <th className="py-2 text-left text-zinc-500 font-heading">Rep Range</th>
                          <th className="py-2 text-left text-zinc-500 font-heading">Training Zone</th>
                        </tr></thead>
                        <tbody>
                          {result.table.map(row => (
                            <tr key={row.pct} className="border-b border-zinc-800 hover:bg-zinc-800/50">
                              <td className="py-3 text-[#E63946] font-heading">{row.pct}%</td>
                              <td className="py-3 text-white font-semibold">{row.weight}</td>
                              <td className="py-3 text-zinc-400">{row.reps} reps</td>
                              <td className="py-3 text-zinc-500 text-xs">
                                {row.pct >= 90 ? 'Max Strength' : row.pct >= 80 ? 'Strength' : row.pct >= 70 ? 'Hypertrophy' : 'Endurance'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div>
                    <SaveResultButton
                      saved={saved}
                      onSave={() => {
                        saveResult('oneRepMax', {
                          value: result.oneRM,
                          label: `${result.oneRM} ${unit} ${selectedExercise?.label} 1RM`,
                          exercise: selectedExercise?.label,
                          weight: parseFloat(weight), reps: parseInt(reps), unit,
                        });
                        setSaved(true);
                      }}
                    />
                  </div>
                </div>
              </EmailResultsGate>
            </div>
          )}
        </div>
      </section>
    </main>
    </ToolSEOWrapper>
  );
}
